/**
 * @fileoverview Gráfico de linha das entradas do fluxo por dia. Uma série só
 * (o título já diz o que é, sem legenda), grade discreta, e ao passar o mouse
 * uma linha guia com o dia e o valor. Para leitor de tela, os mesmos números
 * vão numa tabela escondida.
 */

import React, { useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { i18n } from "../../../translate/i18n";
import { formatDayLong, formatDayShort, formatNumber } from "./format";

const t = (key, options) => i18n.t(`flowResults.${key}`, options);

// Validadas contra o fundo de cada tema (contraste e faixa de luminosidade).
const LINE_COLOR = { light: "#3D5CFD", dark: "#5B7CFF" };

const ChartTooltip = ({ active, payload, classes }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className={classes.chartTooltip}>
      <div className={classes.chartTooltipDay}>{formatDayLong(point.day)}</div>
      <div className={classes.chartTooltipValue}>{t("chart.entries", { count: point.entries })}</div>
    </div>
  );
};

export const EntriesChartSkeleton = ({ classes }) => (
  <div className={classes.skeleton} style={{ width: "100%", height: 240, borderRadius: 10 }} aria-hidden="true" />
);

const EntriesChart = ({ classes, series }) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const lineColor = isDark ? LINE_COLOR.dark : LINE_COLOR.light;
  const grid = isDark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.08)";
  const axis = isDark ? "#a0aec0" : "#4b5563";
  const surface = isDark ? "#14171d" : "#ffffff";

  const hasData = useMemo(() => series.some((point) => point.entries > 0), [series]);

  if (!hasData) return <div className={classes.chartEmpty}>{t("chart.empty")}</div>;

  return (
    <>
      <div className={classes.chartBox} role="img" aria-label={t("chart.title")}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={grid} vertical={false} />
            <XAxis
              dataKey="day"
              tickFormatter={formatDayShort}
              tick={{ fill: axis, fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: grid }}
              interval="preserveStartEnd"
              minTickGap={28}
            />
            <YAxis
              tick={{ fill: axis, fontSize: 11 }}
              tickFormatter={formatNumber}
              tickLine={false}
              axisLine={false}
              width={44}
              allowDecimals={false}
            />
            <Tooltip content={<ChartTooltip classes={classes} />} cursor={{ stroke: axis, strokeOpacity: 0.35, strokeWidth: 1 }} />
            <Line
              // Reta de um dia ao outro: curva suavizada sugeriria valores entre os dias.
              type="linear"
              dataKey="entries"
              stroke={lineColor}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              // Poucos pontos: marca cada dia; muitos: só a linha (o ponto aparece ao passar o mouse).
              dot={series.length <= 31 ? { r: 2.5, fill: lineColor, stroke: surface, strokeWidth: 1 } : false}
              activeDot={{ r: 5, fill: lineColor, stroke: surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <table className={classes.srOnly}>
        <caption>{t("chart.title")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("chart.day")}</th>
            <th scope="col">{t("metrics.entries")}</th>
          </tr>
        </thead>
        <tbody>
          {series.map((point) => (
            <tr key={point.day}>
              <td>{formatDayLong(point.day)}</td>
              <td>{point.entries}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default EntriesChart;
